import { getCurriculum } from '../../content/curriculum';
// import { getFrameIndex } from './roundPlaySelectors';

/**
 * The typical use-case for a `selector` from the reselect library is to create a memo-ized function for reading the
 * Redux store. We are adapting that use case here to create "getter" functions that access the curriculum json
*/
export const getLevelCount = () => getCurriculum().levels.length;

/**
 * Article Selectors
*/

const getArticles = () => getCurriculum().articles;

// get an article by ID
export const getArticle = articleId => getArticles()[articleId];

// get all paragraphs of an article
export const getArticleParagraphs = articleId => getArticle(articleId).paragraphs;

// get a certain paragraph of an article
export const getArticleParagraphByIndex = (articleId, index) => getArticleParagraphs(articleId)[index];

// get the character ID of an article
export const getCharacterIdForArticle = articleId => getArticle(articleId).character;

// get the claim question for an article
export const getClaimQuestion = articleId => getArticle(articleId).claimPrompt;

// get the options for an article's claim question
export const getClaimAnswerOptions = articleId => getArticle(articleId).claimOptions;

// get a certain claim question option by index
export const getClaimAnswerOption = (articleId, optionIndex) => getClaimAnswerOptions(articleId)[optionIndex];

// get the prompt when tagging evidence for an article
export const getEvidencePrompt = articleId => getArticle(articleId).evidencePrompt;

// get the correct answer feedback for an article
export const getCorrectAnswerForEvidencePrompt = articleId => getArticle(articleId).correctAnswerForEvidencePrompt;

// get the correct evidences that can be tagged
export const getCorrectEvidence = articleId => getArticle(articleId).evidence;

// get the minumum number of evidences that need to be tagged
export const getRequiredEvidenceCount = articleId => getArticle(articleId).minimumEvidences;

// get the claim summary for both articles in a level
export const getLevelClaimSummaries = levelIndex =>
  getArticlesInLevel(levelIndex).map(articleId => getArticle(articleId).claimSummary);

// get the intro text (in italics before the rest of the article)
export const getClaimIntroText = articleId => {
  const { introText, rwIntroText } = getArticle(articleId);

  return {
    introText,
    rwIntroText: rwIntroText || introText,
  };
};

// get the messages that appear as corrective feedback for incorrect claim selections
export const getClaimCorrectiveFeedback = articleId => getArticle(articleId).correctiveFeedback;

// get character thumbnail from article ID
export const getCharacterThumbnailFromArticleId = articleId =>
  getCharacterThumbnail(getCharacterIdForArticle(articleId));

// get character thumbnail from article ID
export const getCharacterFullImageFromArticleId = articleId =>
  getCharacterFullImage(getCharacterIdForArticle(articleId));

/**
 * Character Selectors
*/

// get all asset info for a certain character
export const getCharacterAssetInfo = characterId => getCurriculum().characters[characterId];

// get character thumbnail
export const getCharacterThumbnail = characterId => getCharacterAssetInfo(characterId).thumbnail;

// get character full image
export const getCharacterFullImage = characterId => getCharacterAssetInfo(characterId).fullImage;

/**
 * Mastery Tile Selectors
*/

// get all character IDs
export const getAllCharacterIds = () => Object.keys(getCurriculum().characters);

// get all asset info for a certain mastery tile by Id
export const getMasteryTile = tileId => getCurriculum().masteryTiles[tileId];

// get mastery tile display name
export const getMasteryTileName = tileId => getMasteryTile(tileId).name;

// get mastery tile image
export const getMasteryTileImage = tileId => getMasteryTile(tileId).image;

/**
 * Level Selectors
*/

// get the article IDs shown within a certain level
export const getArticlesInLevel = levelIndex => getLevelContent(levelIndex).articles;

// get character ids in this level
export const getLevelCharacterIds = levelIndex =>
  getArticlesInLevel(levelIndex).map(articleId => getArticle(articleId).character);

// get character thumbnails in this level
export const getLevelCharacterThumbnails = levelIndex =>
  getLevelCharacterIds(levelIndex).map(getCharacterThumbnail);

// get entire level content for a certain index
export const getLevelContent = levelIndex => getCurriculum().levels[levelIndex];

// get the text for the two intro screens before reading the first article
export const getLevelIntroTexts = levelIndex => getLevelContent(levelIndex).introTexts;

export const getLevelIntroTextsCount = levelIndex => getLevelIntroTexts(levelIndex).length;

// get the intro text based on current text index
export const getLevelIntroTextByIndex = (levelIndex, introTextIndex) => getLevelIntroTexts(levelIndex)[introTextIndex];

// get the options for the integration option after reading the two articles
export const getLevelIntegrationOptions = levelIndex =>
  getLevelContent(levelIndex).integrationOptions;

// get one of the three integration options by index
export const getIntegrationOption = (levelIndex, optionIndex) =>
  getLevelIntegrationOptions(levelIndex)[optionIndex];

// get the mastery tile earned by completing this level
export const getMasteryTileIdForLevel = levelIndex => getLevelContent(levelIndex).masteryTile;

// get mastery tile name from level index
export const getMasteryTileNameFromLevelIndex = levelIndex =>
  (levelIndex === -1 ?
    'TileBlank' :
    getMasteryTileName(getMasteryTileIdForLevel(levelIndex)));

// get mastery tile image from level index
export const getMasteryTileImageFromLevelIndex = levelIndex =>
  getMasteryTileImage(getMasteryTileIdForLevel(levelIndex));

export const getEvidenceUuid = articleId => getArticle(articleId).evidenceUuid;

export const getClaimUuid = articleId => getArticle(articleId).claimUuid;

export const getIntegrationUuid = levelIndex => getLevelContent(levelIndex).integrationUuid;

export const getClaimSummary = articleId => getArticle(articleId).claimSummary;

export const getIncorrectTagFeedback = articleId => getArticle(articleId).incorrectTagFeedback;

export const getDecisionMomentText = levelIndex => getLevelContent(levelIndex).decisionMomentText;

export const getOnboardingText = () => getCurriculum().onboardingText;

export const getRevealWordsDictionary = () => getCurriculum().revealWords || {};

export const getIntegrationRwIds = levelIndex => getLevelContent(levelIndex).rwIds ?? [];

export const getArticleRwIds = (articleId) => getArticle(articleId).rwIds ?? [];

export const getArticleRwParagraphs = (articleId) => getArticle(articleId).rwParagraphs;

export const getRevealWordsByLevel = (levelIndex) => {
  const articlesInRound = getArticlesInLevel(levelIndex);
  const articlesRwIds = articlesInRound.reduce(
    (acc, articleId) => acc.concat(getArticleRwIds(articleId)),
    [],
  );
  const integrationRwIds = getIntegrationRwIds(levelIndex);
  const revealWordsDictionary = getRevealWordsDictionary();
  const levelRwIds = [...new Set([...articlesRwIds, ...integrationRwIds])];

  return levelRwIds.reduce((acc, id) => {
    if (revealWordsDictionary[id]) {
      acc[id] = revealWordsDictionary[id];
    }

    return acc;
  }, {});
};
